import React, { createContext, useContext, useEffect, useState } from 'react';
import { adminApi, tokenStore } from './adminApi';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = unauthenticated, object = authenticated user
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = tokenStore.get();
    if (!token) {
      setUser(false);
      return;
    }
    adminApi
      .me()
      .then((u) => setUser(u))
      .catch(() => {
        tokenStore.clear();
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    setError('');
    try {
      const data = await adminApi.login(email, password);
      // Two-step flow: server may hand back a 2FA challenge instead of the
      // final access token. The caller (AdminLogin) shows the code prompt.
      if (data?.status === '2fa_required' || data?.status === '2fa_setup_required') {
        return { twofa: data };
      }
      tokenStore.set(data.access_token);
      setUser(data.user);
      return { user: data.user };
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || 'Login failed';
      const formatted = typeof msg === 'string' ? msg : 'Login failed';
      setError(formatted);
      throw new Error(formatted);
    }
  };

  const verify2fa = async (challenge_token, code, trustDevice = false) => {
    setError('');
    try {
      const data = await adminApi.verify2fa(challenge_token, code, trustDevice);
      tokenStore.set(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || '2FA verification failed';
      const formatted = typeof msg === 'string' ? msg : '2FA verification failed';
      setError(formatted);
      throw new Error(formatted);
    }
  };

  const logout = async () => {
    try {
      await adminApi.logout();
    } catch (_) {
      // even if server-side logout fails, clear local state
    }
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, verify2fa, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
